/* eslint-disable @typescript-eslint/no-explicit-any */

import { Dispatch, SetStateAction } from "react";

import {
  ChartTimePeriod,
  ISODuration,
  ChartFilters,
  ChartFilterDefinition,
  FarmEntity,
  DataDescriptorEntity
} from "@ctra/api";

import { Nullable } from "@ctra/utils";

import { FilterSetter, ISODurationSetter } from "@chart";

export interface FilterMetadata {
  filterDefinitions: Array<ChartFilterDefinition>;
  supportedISODurations: Readonly<Array<ISODuration>>;
  supportedfarmIDs: Array<FarmEntity["id"]>;
  flags: { isBackendAggregated: boolean };
  offset?: number;
}

export enum DateMethod {
  custom = "custom",
  iso = "iso"
}

export type SeriesDefinition = {
  available: Record<string, string>;
  active: string[];
};

export type FilterProviderAPI = {
  /**
   * set current ISO duration value (to be converted to a time period)
   */
  setISODuration: ISODurationSetter<any>;
  /**
   * set time unit
   */
  setBucketSize: ISODurationSetter<any>;
  /**
   * set a given filter to a given value
   */
  setFilter: FilterSetter;
  /**
   * reset all filters to their default values
   */
  reset: () => void;
  /**
   * Get the series for a given variant ID
   * @param {DataDescriptorEntity["id"]} variantID
   * @returns {SeriesDefinition}
   */
  getSeries: (variantID?: DataDescriptorEntity["id"]) => SeriesDefinition;
  /**
   * Set the series on the chart
   * @param {DataDescriptorEntity["id"]} variantID
   * @param {SeriesDefinition} series
   */
  setSeries: (variantID: DataDescriptorEntity["id"], series?: SeriesDefinition) => void;
  /**
   * set time period from within custom date range selector
   */
  setTimePeriod: Dispatch<SetStateAction<ChartTimePeriod>>;
  /**
   * Get the metadata for a given variant ID
   * @param {DataDescriptorEntity["id"]} variantID
   * @returns {FilterMetadata}
   */
  getMetadata: (variantID?: DataDescriptorEntity["id"]) => FilterMetadata;
  /**
   * Set filter metadata
   * @param {DataDescriptorEntity["id"]} variantID
   * @param {FilterMetadata} metadata
   */
  setMetadata: (variantID: DataDescriptorEntity["id"], metadata?: FilterMetadata) => void;
  /**
   * set the current date range deciding method
   */
  setCurrentDateMethod: Dispatch<SetStateAction<DateMethod>>;
};

export type FilterProviderProps = {
  series?: Record<DataDescriptorEntity["id"], string[]>;
  filters?: Record<DataDescriptorEntity["id"], ChartFilters>;
  isoDuration?: Nullable<ISODuration>;
  timePeriod?: ChartTimePeriod;
  bucketSize?: ISODuration;
  durationOptions?: Readonly<Array<ISODuration>>;
  /**
   * Force values to stay the same
   */
  currentValues?: {
    isoDuration?: Nullable<ISODuration>;
    timePeriod?: ChartTimePeriod;
    bucketSize?: ISODuration;
  };
};

export type ContextType = {
  filters: Record<DataDescriptorEntity["id"], ChartFilters>;
  // global filters
  timePeriod: ChartTimePeriod;
  isoDuration: Nullable<ISODuration>;
  bucketSize: ISODuration;
  currentDateMethod?: DateMethod;
  // API
  api: FilterProviderAPI;
};
